import { createSelector } from '@ngrx/store';
import {
  AuthorManagementState,
  reducer as authermanagementReducer,
} from 'projects/author-management/src/lib/author-management.reducer';
import { EditorState, reducer as editorReducer } from 'projects/editor/src/lib/editor.reducer';
import { ProjectState, reducer as projectReducer } from '../editor/project/project.reducer';
import {
  ReferencesState,
  referenceReducer,
} from 'projects/reference-management/src/lib/store/references.reducer';
import { ExportState, exportReducer } from 'export';

export const referenceFeatureKey = 'reference-app';

export const extractTitle = (doc): string | void => {
  const header = doc?.content && doc?.content.length > 0 ? doc.content[0] : undefined;
  if (header?.type === 'header') {
    const titleNode = header.content && header.content[0];
    if (titleNode) {
      return titleNode.content?.map((n) => n.text).join('');
    }
  }

  return '';
};

export interface ReferenceState {
  sharedEditor: EditorState;
  sharedAuthorManagement: AuthorManagementState;
  sharedExport: ExportState;
  sharedReferences: ReferencesState;
}

export const selectAuthorManagementState = (state): AuthorManagementState =>
  state.sharedAuthorManagement;

export const selectSelectedAuthor = createSelector(
  selectAuthorManagementState,
  (state: AuthorManagementState) => state.selectedAuthor,
);

export const selectAuthors = createSelector(
  selectAuthorManagementState,
  (state: AuthorManagementState) => state.authors,
);

export const selectedDocument = (state): ProjectState => state.sharedProject;

export const selectDocumentModified = createSelector(
  selectedDocument,
  (state: ProjectState) => state.lastModified,
);

export const selectEditorState = (state): EditorState => state.sharedEditor;

export const selectReferencesState = (state): ReferencesState => state.sharedReferences;

export const selectDocumentVersion = createSelector(
  selectEditorState,
  (state: EditorState) => state.instances[0]?.version,
);

export const selectDocumentId = createSelector(
  selectEditorState,
  (state: EditorState) => state.instances[0]?.id,
);

/** Gets the active editor instance. */
export const selectActiveEditor = createSelector(
  selectEditorState,
  (state: EditorState) => state.instances[0],
);

export const selectDocumentTitle = createSelector(selectEditorState, (state: EditorState) =>
  extractTitle(state.instances[0]?.state.doc),
);

/**
 * checks weather the document is dirty or not
 */
export const selectIsDirty = createSelector(selectEditorState, (state: EditorState) => state.dirty);

export const selectExport = (state): ExportState => state.sharedExport;

export const appReducer = {
  sharedEditor: editorReducer,
  sharedAuthorManagement: authermanagementReducer,
  sharedProject: projectReducer,
  sharedExport: exportReducer,
  sharedReferences: referenceReducer,
};

export const getUser = createSelector(
  selectAuthorManagementState,
  (state: AuthorManagementState) => state.options[0],
);

export const getDocumentContext = createSelector(
  selectAuthorManagementState,
  (state: AuthorManagementState) => state.options[0]?.activeDocumentContext,
);

/**
 * Returns true when the project has references and at least one is not
 * cited anywhere in the document.  When there are no references at all
 * the document is considered "clean" (nothing to cite).
 */
export const selectHasUncitedReferences = createSelector(
  selectReferencesState,
  (state: ReferencesState) => {
    if (!state || state.references.length === 0) return false;

    const citedIds = new Set(state.citations.flatMap((c) => c.citationItems.map((ci) => ci.id)));

    return state.references.some((ref) => !citedIds.has(ref.key));
  },
);
